// src/pages/Customer-items/CustomerItems.tsx
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import { Table } from "../../../components/shared";
import { RootState } from "../../../redux/store";
import useSearchAndFilter from "../../../hooks/useSearchAndFilter";
import { Loader } from "../../../components/loaders/loaders";
import { EXPORT_CUSTOMER_ITEMS, EXPORT_ORDERS, RESEND_INVITATION_KEY } from "../../../apis/end-points";

import { AddIcon, FilterIcon, FilterIconWhite } from "../../../assets";
import FilterSideMenu from "../../../components/shared/FilterSideMenu";
import { useEffect, useState } from "react";
import { Badge } from "@mui/material";
import { getApi } from "../../../apis/api";
import ExportExcelButton from "../../../components/shared/ExportExcel";
import { DEFAULT_PAGE_SIZE } from "../../../utils/constants";
import { CUSTOMER_MANAGEMENT_MESSAGES } from "../../../utils/message";

/** ========= Columns aligned to customer item list payload ========= */
const columns = [
  { key: "count", header: "S.", isSortable: false, className: "count" },
  {
    key: "customerName",
    header: "Customer Name",
    isSortable: true,
    className: "dark",
  },
  {
    key: "subCustomerName",
    header: "Sub Customer Name",
    isSortable: true,
    className: "dark",
  },
  {
    key: "itemName",
    header: "Description",
    isSortable: true,
    upperCase: true,
    className: "dark",
  },
  {
    key: "packageType",
    header: "Handling Unit Type",
    isSortable: true,
    className: "dark",
  },
  { key: "itemSKU", header: "Item SKU", isSortable: true, className: "dark" },
  { key: "weight", header: "Weight", isSortable: true, className: "dark" },
  {
    key: "dimension",
    header: "Dimension (L×W×H)",
    isSortable: false,
    className: "dark",
  },
  { key: "unit", header: "Unit", isSortable: true, className: "dark" },
  {
    key: "itemClass",
    header: "Item Class",
    isSortable: true,
    className: "dark",
  },
  {
    key: "itemNmfcNo",
    header: "Item Nmfc No.",
    isSortable: true,
    className: "dark",
  },
  { key: "comments", header: "Comments", isSortable: false, className: "dark" },
];

/** ========= Table config ========= */
const tableConfig = {
  edit: {
    apiUrl: "/customer-items/edit/", // navigate to /customer-items/edit/:id
    urlID: "id",
    stateData: true, // pass the row in navigation state
  },
  delete: {
    apiUrl: "/customer/saveddetails/items/", // delete /:id
    urlID: "id",
    successMessage: CUSTOMER_MANAGEMENT_MESSAGES.ITEM_DELETE_SUCCESS,
    heading: "Delete Item!",
    para: "delete",
    secondRowName: "",
    staticContent: "this item",
  },
  active: {
    apiUrl: "/customer/customer-user/",
    urlID: "id",
    successMessage: CUSTOMER_MANAGEMENT_MESSAGES.CUSTOMER_USER_ACTIVE_LABEL,
    heading: "Customer User!",
    para: "",
    rowName: "firstName",
    secondRowName: "lastName",
    onboardedMessage: "Customer User",
  },
  view: {
    apiUrl: "/customer-users/edit/",
    urlID: "id",
  },
  resendInvite: {
    apiUrl: RESEND_INVITATION_KEY,
    urlID: "email",
    successMessage: CUSTOMER_MANAGEMENT_MESSAGES.INVITATION_RESENT_SUCCESS,
    heading: "Resend Invitation!",
    para: "",
    extra: "Customer user",
    rowName: "customerName",
    secondRowName: "",
  },
};

/** ========= Mapper aligned to the item list response ========= */
type Item = {
  id: string;
  description?: string;
  packageType?: string;
  skuNumber?: string;
  weight?: string | number;
  length?: string | number;
  width?: string | number;
  height?: string | number;
  unit?: string; // IN | CM
  itemClass?: string;
  nmfc?: string;
  comments?: string;
  count?: number | string;
  customer?: {
    id?: string | null;
    name?: string | null;
    customerLevel?: string | null;
    parentCustomer?: { id?: string | null; name?: string | null } | null;
  } | null;
  stackable?: boolean;
};

const formatDim = (l?: any, w?: any, h?: any) => {
  const vals = [l, w, h].map((v) => (v ?? "").toString().trim());
  return vals.every((v) => v !== "") ? `${vals[0]}×${vals[1]}×${vals[2]}` : "";
};

const mapItemsData = (items: Item[]) =>
  (items || []).map((it) => {
    const hasParent = !!it?.customer?.parentCustomer;
    const customerName =
      hasParent && it?.customer?.customerLevel === "third"
        ? it?.customer?.parentCustomer?.name ?? ""
        : it?.customer?.name ?? "";
    const subCustomerName =
      hasParent && it?.customer?.customerLevel == "third"
        ? it?.customer?.name ?? ""
        : "";
    return {
      id: it.id,
      count: it.count ?? "",
      // keep raw ids for edit prefill compatibility
      customerId: it?.customer?.id || "",
      customerName,
      subCustomerName,
      packageType: it?.packageType || "",
      itemName: it?.description || "",
      itemSKU: it?.skuNumber || "",
      weight: (it?.weight ?? "")?.toString(),
      dimension: formatDim(it?.length, it?.width, it?.height),
      lengthVal: it?.length ?? "",
      widthVal: it?.width ?? "",
      heightVal: it?.height ?? "",
      unit: it?.unit || "",
      itemClass: it?.itemClass || "",
      itemNmfcNo: it?.nmfc || "",
      comments: it?.comments || "",
      // optional: expose parent id if you ever need it later (won’t affect table)
      parentCustomerId: it?.customer?.parentCustomer?.id || "",
      customer: it?.customer,
      stackable: it?.stackable,
    };
  });

/** ========= Component ========= */
const CustomerItems = () => {
  const navigate = useNavigate();

  // cache-buster (kept as-is to avoid hampering existing flows)
  const [noCacheQS, setNoCacheQS] = useState<string>(() => ``);

  const {
    data,
    loading,
    setLoading,
    fetchFilteredData,
    isShowFilterModal,
    closeOpenfilterModal,
    selectStatus,
    setSelectStatus,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    admin,
    setAdmin,
    selectedRoles,
    setSelectedRoles,
    rolesDropdownData,
    setRolesDropdownData,
    filterApplied,
    setFilterApplied,
    customerName,
    setCustomerName,
    description,
    setDescription,
    handlingUnitSelected,
    setHandlingUnitSelected,
    handlingUnitDropDown,
    setHandlingUnitDropDown,
    clearCurrentFilters,
    fetchWithCurrentFilters,
    captureAppliedFilters,
  } = useSearchAndFilter<Item>({
    apiUrl: "/customer/saveddetails/customeritemlist",
    limit: DEFAULT_PAGE_SIZE,
    mapUserData: mapItemsData,
    extentedUrl: noCacheQS,
    screenKey: "customerItem",
  });

  const { userRole } = useSelector((state: RootState) => state.auth);
  const { userRoles } = useSelector((state: RootState) => state.staticData);
  const [filterData, setFilterData] = useState<any>({});

  const handleNavigateUser = (url: string) => {
    navigate(url);
  };

  const GetFilterData = async () => {
    const response = await getApi("/service/packagetype?from=order");

    if (response.success && Array.isArray(response.data)) {
      const transformedData = {
        handlingType: response.data.map((item: any) => ({
          id: item.id,
          name: item.description,
        })),
      };

      setFilterData(transformedData);
    }
  };


  useEffect(() => {
    GetFilterData();
  }, []);

  // keep behavior same; just pass through your existing fetch
  const fetchFilteredDataNoCache = (...args: any[]) => {
    // setNoCacheQS(`&_ts=${Date.now()}`);
    // @ts-ignore passthrough args as your Table expects
    return fetchFilteredData(...args);
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div
      className={`tableWrap ${isShowFilterModal ? "tableWrap--filterOpen" : ""
        }`}
    >
      <div className="tableWrap__head tableWrap__head--end">
        {localStorage.getItem("roles") !== "superadmin" && (
          <div className="btn__group">
            {filterApplied ? (
              <Badge badgeContent=" " color="primary">
                <button
                  className={
                    !isShowFilterModal ? "btn btn__white" : "btn btn__primary"
                  }
                  onClick={closeOpenfilterModal}
                >
                  {!isShowFilterModal ? <FilterIcon /> : <FilterIconWhite />}
                  Filter
                </button>
              </Badge>
            ) : (
              <button
                className={
                  !isShowFilterModal ? "btn btn__white" : "btn btn__primary"
                }
                onClick={() => {
                  closeOpenfilterModal();
                }}
              >
                {!isShowFilterModal ? <FilterIcon /> : <FilterIconWhite />}
                Filter
              </button>
            )}

            <FilterSideMenu
              isShowFilterModal={isShowFilterModal}
              closeOpenfilterModal={closeOpenfilterModal}
              isStatusHide={true}
              isRoleHide={true}
              isOrderStatusHide={true}
              isOrderTypeHide={true}
              isAgentTypeHide={true}
              isHubHide={true}
              showCustomerName={true}
              customerName={customerName}
              setCustomerName={setCustomerName}
              showDescription={true}
              setDescription={setDescription}
              description={description}
              isServiceHide={true}
              isCustomerTypeHide={false}
              isAllCustomerTypeHide={true}
              fetchFilteredData={fetchFilteredData}
              onFiltersApplied={captureAppliedFilters}
              filterData={filterData}
              filterApplied={filterApplied}
              setFilterApplied={setFilterApplied}
              handlingUnitDropDownData={handlingUnitDropDown}
              handlingUnitSelected={handlingUnitSelected}
              setHandlingUnitSelected={setHandlingUnitSelected}
              setHandlingUnitDropDownData={setHandlingUnitDropDown}
              showHandlingType={true}
              table="customerItem"
              clearFiltersFromStorage={clearCurrentFilters}
            />
            <ExportExcelButton
              url={EXPORT_CUSTOMER_ITEMS}
              fileName="Customer_Items_Export.xlsx"
              label="Export All"
            />

            <button
              className="btn btn__white"
              onClick={() => handleNavigateUser("/customer-items/add")}
            >
              <AddIcon />
              Create New
            </button>
          </div>
        )}
      </div>

      <Table
        columns={columns}
        data={data}
        setLoading={setLoading}
        onEditNaviagte={tableConfig.edit}
        onDeleteConfig={tableConfig.delete}
        onActiveConfig={tableConfig.active}
        onViewConfig={tableConfig.view}
        resendInviteConfig={tableConfig.resendInvite}
        viewIcon={userRole === "superadmin" && true}
        deleteIcon={true}
        setFilterApplied={setFilterApplied}
        filterApplied={filterApplied}
        editIcon={true}
        activeIcon={
          (userRole === "admin" ||
            userRole === "customersuperadmin" ||
            userRole === "manager") &&
          true
        }
        getApiData={fetchWithCurrentFilters}
        table={"customerUser"}
      />
    </div>
  );
};

export default CustomerItems;
